/**
 * Utility for managing Google Consent Mode v2 for GDPR/CCPA compliance.
 * Central hub for gating analytics/tracking behind user consent.
 */

import { setReplayConsent } from '../lib/sentry';
import { initPostHog, optIn, optOut } from '../lib/posthog';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function gtag(...args: any[]): void;
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
  }
}

/**
 * Load TrustedForm script dynamically (only after consent).
 */
const loadTrustedForm = () => {
  if (document.getElementById('trustedform-script')) return;
  const tf = document.createElement('script');
  tf.id = 'trustedform-script';
  tf.type = 'text/javascript';
  tf.async = true;
  tf.src =
    (location.protocol === 'https:' ? 'https' : 'http') +
    '://api.trustedform.com/trustedform.js?field=xxTrustedFormCertUrl&l=' +
    Date.now() +
    Math.random();
  document.head.appendChild(tf);
};

const loadGoogleTagManager = () => {
  if (document.getElementById('google-tag-manager-script')) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const gtm = document.createElement('script');
  gtm.id = 'google-tag-manager-script';
  gtm.async = true;
  gtm.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-PMDJFC5R';
  document.head.appendChild(gtm);
};

/**
 * Enable Sentry session replay after consent.
 */
const enableSentryReplay = () => {
  setReplayConsent(true);
};

/**
 * Disable Sentry session replay when consent is revoked.
 */
const disableSentryReplay = () => {
  setReplayConsent(false);
};

export const CONSENT_STORAGE_KEY = 'fairday_cookie_consent';
/** How the stored verdict was reached — informs the CPRA "Do Not Sell" UI. */
export const CONSENT_SOURCE_KEY = 'fairday_cookie_consent_source';
export type ConsentSource = 'explicit' | 'default' | 'gpc';

/** EEA members on Atlantic (not Europe/*) IANA zones — Iceland, Faroe,
 *  plus Spanish/Portuguese island zones. */
const OPT_IN_ATLANTIC_ZONES = new Set([
  'Atlantic/Reykjavik',
  'Atlantic/Faroe',
  'Atlantic/Canary',
  'Atlantic/Madeira',
  'Atlantic/Azores',
]);

/**
 * Whether a visitor's IANA timezone puts them in an opt-in consent regime
 * (GDPR / UK GDPR / EEA → banner BEFORE any pixel loads). Everywhere else
 * (US/CCPA-CPRA, CA, AU…) is opt-out: pixels may load by default as long as
 * an opt-out path exists (footer "Do Not Sell or Share" + GPC honoured).
 *
 * Unknown timezone → opt-in, the safe regime. Timezone is a heuristic
 * (travellers/VPNs misclassify) but errs at the individual level, not the
 * regime level, and needs no geo-IP service.
 */
export const isOptInRegion = (timeZone: string | null | undefined): boolean => {
  if (!timeZone) return true;
  return timeZone.startsWith('Europe/') || OPT_IN_ATLANTIC_ZONES.has(timeZone);
};

const detectTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

const hasGlobalPrivacyControl = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true;
};

/**
 * Decide consent for a first-time visitor who hasn't answered the banner.
 *  - GPC signal → 'denied' (legally binding opt-out under CPRA; honoured silently)
 *  - opt-in region (EU/UK/EEA/unknown) → null → show the banner, nothing loads
 *  - everywhere else → 'granted' by default (opt-out regime)
 */
export const resolveImplicitConsent = (
  timeZone: string | null | undefined = detectTimeZone(),
  gpc: boolean = hasGlobalPrivacyControl(),
): { status: 'granted' | 'denied'; source: ConsentSource } | null => {
  if (gpc) return { status: 'denied', source: 'gpc' };
  if (isOptInRegion(timeZone)) return null;
  return { status: 'granted', source: 'default' };
};

export const getConsentSource = (): ConsentSource | null => {
  const raw = localStorage.getItem(CONSENT_SOURCE_KEY);
  return raw === 'explicit' || raw === 'default' || raw === 'gpc' ? raw : null;
};

const syncGtagConsent = (status: 'granted' | 'denied') => {
  if (typeof gtag !== 'function') return;
  gtag('consent', 'update', {
    'ad_storage': status,
    'ad_user_data': status,
    'ad_personalization': status,
    'analytics_storage': status
  });
};

/**
 * Called on app boot — initializes consent-gated services
 * only if user previously consented (or is in an opt-out region where
 * consent is granted by default until they opt out).
 */
export const initConsentGatedServices = () => {
  let consent = localStorage.getItem(CONSENT_STORAGE_KEY);
  if (consent === null) {
    const implicit = resolveImplicitConsent();
    if (implicit) {
      localStorage.setItem(CONSENT_STORAGE_KEY, implicit.status);
      localStorage.setItem(CONSENT_SOURCE_KEY, implicit.source);
      consent = implicit.status;
    }
  }
  if (consent === 'granted') {
    // index.html defaulted Consent Mode to denied; a default-granted first
    // visit needs the update BEFORE GTM boots so ad tags fire on page one.
    syncGtagConsent('granted');
    initPostHog();
    loadGoogleTagManager();
    // The Sentry loader reads persisted consent when it initializes during an
    // idle period. Defer third-party form code so it cannot compete with LCP.
    window.setTimeout(loadTrustedForm, 2000);
  }
  // If denied or not yet responded, other services remain inactive
};

export const updateConsent = (granted: boolean, source: ConsentSource = 'explicit') => {
  const status = granted ? 'granted' : 'denied';

  // 1. Save to local storage so the index.html script picks it up on next load
  localStorage.setItem(CONSENT_STORAGE_KEY, status);
  localStorage.setItem(CONSENT_SOURCE_KEY, source);

  // 2. Push the update to GTM immediately
  syncGtagConsent(status);

  // 3. Trigger a custom event for GTM to fire tags that were waiting
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'consent_updated',
      consent_status: status
    });
  }

  // 4. Gate remaining analytics services on consent
  if (granted) {
    initPostHog();
    optIn();
    loadGoogleTagManager();
    enableSentryReplay();
    loadTrustedForm();
  } else {
    optOut();
    disableSentryReplay();
  }
};

export const hasRespondedToConsent = (): boolean => {
  return localStorage.getItem(CONSENT_STORAGE_KEY) !== null;
};

export const isConsentGranted = (): boolean => {
  return localStorage.getItem(CONSENT_STORAGE_KEY) === 'granted';
};
